import { useState, useCallback } from 'react'
import {
  onboardingControllerFindOrCreateTutorialEndpoint,
  onboardingControllerUpdateTutorialEndpoint,
  onboardingControllerGiveRewards
} from 'api/onboarding-custom'
import {
  TutorialResponseDtoState,
  UpdateTutorialDtoAction
} from 'api/generated/schemas'
import { useUserInfo } from 'utils/hooks/use-userInfo'

export type ChecklistRewardType =
  | 'checklist_primeira_leitura'
  | 'checklist_area_conquistas'
  | 'checklist_plantar_arvore'

type RewardAmounts = {
  coins?: number
  waterDrops?: number
  points?: number
}

type UseChecklistRewardsReturn = {
  hasReceivedReward: (rewardType: ChecklistRewardType) => Promise<boolean>
  markRewardAsReceived: (rewardType: ChecklistRewardType) => Promise<void>
  giveReward: (
    rewardType: ChecklistRewardType,
    amounts: RewardAmounts
  ) => Promise<void>
  checkRewardStatus: (rewardType: ChecklistRewardType) => Promise<boolean>
  isLoading: boolean
}

export const useChecklistRewards = (): UseChecklistRewardsReturn => {
  const { user } = useUserInfo()
  const [isLoading, setIsLoading] = useState(false)

  const checkRewardStatus = useCallback(
    async (rewardType: ChecklistRewardType): Promise<boolean> => {
      if (!user?.reader?.id) {
        return false
      }

      try {
        const response = await onboardingControllerFindOrCreateTutorialEndpoint(
          {
            name: rewardType
          }
        )

        if (response.status === 201 && response.data) {
          return response.data.state === TutorialResponseDtoState.completed
        }

        return false
      } catch {
        return false
      }
    },
    [user?.reader?.id]
  )

  const hasReceivedReward = useCallback(
    async (rewardType: ChecklistRewardType): Promise<boolean> => {
      return await checkRewardStatus(rewardType)
    },
    [checkRewardStatus]
  )

  const giveReward = useCallback(
    async (
      rewardType: ChecklistRewardType,
      amounts: RewardAmounts
    ): Promise<void> => {
      if (!user?.reader?.id) {
        return
      }

      setIsLoading(true)

      try {
        const userId =
          typeof user.reader?.id === 'string'
            ? parseInt(user.reader?.id, 10)
            : user.reader?.id

        const rewardResponse = await onboardingControllerGiveRewards({
          userId,
          coins: amounts.coins,
          waterDrops: amounts.waterDrops,
          points: amounts.points
        })

        if (rewardResponse.status === 201) {
          const createResponse =
            await onboardingControllerFindOrCreateTutorialEndpoint({
              name: rewardType
            })

          if (createResponse.status === 201 && createResponse.data) {
            await onboardingControllerUpdateTutorialEndpoint({
              id: createResponse.data.id,
              action: UpdateTutorialDtoAction.COMPLETE
            })
          }
        }
      } finally {
        setIsLoading(false)
      }
    },
    [user?.reader?.id]
  )

  const markRewardAsReceived = useCallback(
    async (rewardType: ChecklistRewardType): Promise<void> => {
      if (!user?.reader?.id) {
        return
      }

      setIsLoading(true)

      try {
        const createResponse =
          await onboardingControllerFindOrCreateTutorialEndpoint({
            name: rewardType
          })

        if (createResponse.status === 201 && createResponse.data) {
          await onboardingControllerUpdateTutorialEndpoint({
            id: createResponse.data.id,
            action: UpdateTutorialDtoAction.COMPLETE
          })
        }
      } finally {
        setIsLoading(false)
      }
    },
    [user?.reader?.id]
  )

  return {
    hasReceivedReward,
    markRewardAsReceived,
    giveReward,
    checkRewardStatus,
    isLoading
  }
}
